import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAuthSession } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { useAuthenticator } from '@aws-amplify/ui-react';

/**
 * AuthCallback component is responsible for handling authentication
 * callback logic. It checks the authentication session, updates the 
 * status and error messages based on the result, and navigates the 
 * user to the appropriate route. It displays loading, success, or 
 * error states based on the authentication process.
 */
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // useEffect(() => {
  //   const processAuth = async () => {
  //     try {
  //       const session = await fetchAuthSession({ forceRefresh: true });

  //       console.log("session", session);

  //       if (!session || !session.tokens) {
  //         console.error('No valid session found after auth');
  //         setStatus('error');
  //         setErrorMessage('No valid authentication session found');
  //         return;
  //       }
        
  //       setStatus('success');
        
  //       // Redirect to workspaces after a short delay
  //       setTimeout(() => {
  //         navigate('/workspaces');
  //       }, 1500);
  //     } catch (error) {
  //       console.error('Authentication error:', error);
  //       setStatus('error');
  //       setErrorMessage(error instanceof Error ? error.message : 'Unknown authentication error');
  //     }
  //   };
    
  //   processAuth();
  // }, [navigate, searchParams, setIsAuthenticated, setUser]);

  const { user } = useAuthenticator(ctx => [ctx.user]);

  useEffect(() => {
    if (user) {
      // Check for pending invitation token
      const pendingInviteToken = localStorage.getItem('pendingInviteToken');
      
      if (pendingInviteToken) {
        // Redirect back to invitation page to complete the flow
        navigate(`/invite/${pendingInviteToken}`);
      } else {
        // Normal flow - redirect to workspaces
        navigate('/workspaces');
      }
    }
  }, [user, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white dark:bg-gray-900">
      <div className="w-full max-w-md p-8 space-y-8 bg-white dark:bg-gray-800 rounded-lg shadow-md text-center">
        {user === null && (
          <>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Processing Authentication</h2>
            <div className="flex justify-center mt-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Please wait while we complete your authentication...</p>
          </>
        )}
        
        {user !== null && (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
              <svg className="h-6 w-6 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Authentication Successful</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">You are now signed in. Redirecting to your workspaces...</p>
          </>
        )}
        
        {/* not sure what to do here */}
        {status === 'error' && (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
              <svg className="h-6 w-6 text-red-600 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Authentication Failed</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">{errorMessage}</p>
            <button 
              onClick={() => navigate('/')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Return to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;

export function useAuthSession() {
  const [session, setSession] = useState<any>(null);

  useEffect(() => {
    const update = async () => {
      try { setSession(await fetchAuthSession({ forceRefresh: true })); }
      catch {}
    };
    update();

    const listener = ({ payload }: any) => {
      console.log("event", payload.event);
      console.log(payload);
      if (['signIn','tokenRefresh','signOut'].includes(payload.event)) update();
    };
    const stop = Hub.listen('auth', listener);
    return () => stop();
  }, []);

  return session;
}